/* sts-state-quiz-bridge.js — Q~ resume persistence.
 *
 * Spec: docs/new-theme-v4/specs/02-session-states-accounts-routing-spec.md §7.4.
 *
 * The problem
 * -----------
 * assets/sts-liquid-quiz.js checkpoints quiz progress to sessionStorage under
 * `slqQuizProgress` (:811-853). sessionStorage dies with the tab, so the
 * site-wide "we saved your answers" strip cannot exist: by the time someone
 * comes back to the homepage tomorrow, there is nothing left to offer them.
 *
 * What this does
 * --------------
 * Mirrors that one key into localStorage `sts_quiz_progress` in the envelope
 * the state engine reads ({v, answers, currentIndex, version, hadCode, answered,
 * total, savedAt}, 30-day TTL), and rehydrates it when a fresh tab asks for the
 * session key and finds nothing. The quiz engine is untouched: it still saves
 * and restores exactly as it does today, so `firstIncompleteQuestionIndex()`
 * still decides where the quiz reopens and the resume-at-the-right-question
 * behaviour is unchanged.
 *
 * Why a bridge rather than an edit to the engine
 * ----------------------------------------------
 * `assets/sts-liquid-quiz.js` is shared: spec 08 changes `setTypeColors()` in
 * the same file for the token layer. Two parallel workstreams editing one 2,700
 * line file is how work gets lost. When 08 has landed, the clean move is to fold
 * this file's persist/rehydrate into the engine's saveProgress/restoreProgress
 * and delete this file; the storage format is identical either way.
 *
 * Identity answers are never stored long-term
 * -------------------------------------------
 * q17 (full name), q18 (birth month/year) and q19 (ZIP) are stripped on write
 * AND filtered again on read, so a record written before this rule existed
 * cannot reintroduce them. This is deliberate and load-bearing: the engine's own
 * comment at :2578-2584 records that shared and kiosk browsers leaked a previous
 * visitor's name and birth date. Those three questions are re-asked on resume —
 * the engine rewinds to the first unanswered question by itself.
 *
 * Load with defer, before the quiz engine. Site-wide is correct: the typed-at
 * stamp below has to catch a quiz completion wherever it happens.
 */
(function () {
  'use strict';

  var w = window;
  var proto = w.Storage && w.Storage.prototype;
  if (!proto || !proto.getItem) return;

  var SESSION_KEY = 'slqQuizProgress';   // what assets/sts-liquid-quiz.js uses
  var LONG_KEY = 'sts_quiz_progress';    // what the state engine reads
  var TYPED_KEY = 'sts_typed_at';
  var TYPE_KEY = 'customerSkinType';
  var FRESH_KEY = 'sts_fresh_type';       // read by snippets/sts-head-state.liquid
  var ENVELOPE_VERSION = 1;
  var TTL_MS = 30 * 86400000;            // 30 days (02-Q9)

  /* lib/quiz-core/user-info.ts deriveUserInfoFromAnswers reads exactly these
     three, and they are the only answers that identify a person. Named
     constant, applied twice, on purpose. */
  var IDENTITY_QUESTION_IDS = ['q17', 'q18', 'q19'];

  var rawGet = proto.getItem;
  var rawSet = proto.setItem;
  var rawDel = proto.removeItem;

  function localGet(k) { try { return rawGet.call(w.localStorage, k); } catch (e) { return null; } }
  function localSet(k, v) { try { rawSet.call(w.localStorage, k, v); } catch (e) { /* blocked or full */ } }
  function localDel(k) { try { rawDel.call(w.localStorage, k); } catch (e) { /* blocked */ } }
  function parse(raw) { try { return JSON.parse(raw); } catch (e) { return null; } }

  function withoutIdentity(answers) {
    var out = {};
    var keys = Object.keys(answers || {});
    for (var i = 0; i < keys.length; i++) {
      if (IDENTITY_QUESTION_IDS.indexOf(keys[i]) === -1) out[keys[i]] = answers[keys[i]];
    }
    return out;
  }

  function countAnswered(answers) {
    /* A key is only written on Continue, so a skipped optional ('' or [])
       is answered too; the engine's resume point counts the same way. */
    var keys = Object.keys(answers || {});
    var n = 0;
    for (var i = 0; i < keys.length; i++) if (answers[keys[i]] != null) n++;
    return n;
  }

  /* `total` is the number of questions currently VISIBLE, which moves as
     conditional questions appear and disappear — so it can only come from the
     engine's own count. It renders it on every question:
     "Question 7 of 19" in [data-slq-progress-count]. Reading it there keeps the
     engine untouched. When the counter is not on the page (a save that somehow
     precedes the first render) the previous stored total is kept rather than
     invented, and a record with total 0 simply shows no percentage. */
  function currentTotal(previous) {
    try {
      var el = document.querySelector('[data-slq-progress-count]');
      var m = el && /of\s+(\d+)/.exec(el.textContent || '');
      if (m) return parseInt(m[1], 10);
    } catch (e) { /* no DOM yet */ }
    return previous || 0;
  }

  function readLong() {
    var rec = parse(localGet(LONG_KEY));
    if (!rec) return null;
    if (rec.v !== ENVELOPE_VERSION || !(Date.now() - rec.savedAt < TTL_MS)) {
      localDel(LONG_KEY);          // expired records are deleted on read (§7.1)
      return null;
    }
    return rec;
  }

  /* sessionStorage -> localStorage, on every answer commit. */
  function persist(value) {
    var saved = parse(value);
    if (!saved || !saved.answers) return;
    var answers = withoutIdentity(saved.answers);
    var previous = readLong();
    localSet(LONG_KEY, JSON.stringify({
      v: ENVELOPE_VERSION,
      answers: answers,
      currentIndex: saved.currentIndex,
      version: saved.version,
      hadCode: saved.hadCode,
      answered: countAnswered(answers),
      total: currentTotal(previous && previous.total),
      savedAt: Date.now()
    }));
    if (w.STS && w.STS.refresh) w.STS.refresh();
  }

  /* localStorage -> the shape the engine expects, for a tab that has no
     session copy. The identity deny-list is applied a second time here, so an
     older record cannot put a name back on screen. */
  function rehydrate() {
    var rec = readLong();
    if (!rec) return null;
    return JSON.stringify({
      answers: withoutIdentity(rec.answers),
      currentIndex: rec.currentIndex,
      version: rec.version,
      hadCode: rec.hadCode
    });
  }

  /* A finished quiz dates the type. `sts.typed_at` on the customer record is the
     canonical one (WS01) and sts-state.js overwrites this from the server for a
     signed-in customer; this is what gives a guest a check-in date at all.
     Guarded on a live widget token so it fires only for a submission that just
     happened in this page: snippets/quiz.liquid writes customerSkinType from the
     metafield on every logged-in page load, and stamping "now" there would reset
     a check-in clock that is genuinely six months old. */
  function stampTypedAt(type) {
    if (!(w.SLQ && w.SLQ.state && w.SLQ.state.widgetToken)) return;
    localSet(TYPED_KEY, new Date().toISOString());
    /* Only the code this submission returned: legacy scripts also write the
       account's (older) code to customerSkinType, and must not be taken for
       a new result. */
    if (String(type).trim().toUpperCase() === String(w.SLQ.state.skinType || '').trim().toUpperCase()) noteFreshType(type);
    localDel(LONG_KEY);
    /* A new result is a new routine: this visit's copy of the old one
       (STS.visitRoutine, sts-state.js) must not stand in for it. */
    try { rawDel.call(w.sessionStorage, 'sts.addrt'); } catch (e) { /* blocked */ }
  }
  /* A quiz a SIGNED-IN customer just finished here outranks the account's
     customer_info.skinType until the backend's write shows up in Liquid
     (snippets/sts-head-state.liquid, "THE LATEST QUIZ WINS"; sts-state.js
     drops the note once the account agrees). A guest needs none: their type
     IS customerSkinType. Exported for the results page's "apply new results",
     which changes the account's type the same way. */
  function noteFreshType(type) {
    var srv = w.__STS_SERVER__ || {};
    var code = String(type || '').trim().toUpperCase();
    if (!srv.loggedIn || !srv.customerId || !/^[DO][SR][PN][TW]$/.test(code)) return;
    localSet(FRESH_KEY, JSON.stringify({ t: code, at: Date.now(), c: String(srv.customerId) }));
    /* After the write this hook shadows, so the resolver sees the new code. */
    w.setTimeout(function () { if (w.STS && w.STS.refresh) w.STS.refresh(); }, 0);
  }

  /* The skin type changed on this device (a retake by any route): a visit
     copy made for the old type goes too. The simulator's copy follows its own
     type (sts-state.js, on sts:state), not the device's. */
  function dropVisit(type) {
    var v = parse(rawGet.call(w.sessionStorage, 'sts.addrt'));
    if (v && v.o !== 'sim' && v.t && v.t !== String(type).trim().toUpperCase()) rawDel.call(w.sessionStorage, 'sts.addrt');
  }

  proto.getItem = function (key) {
    if (key === SESSION_KEY && this === w.sessionStorage) {
      var own = rawGet.call(this, key);
      if (own != null) return own;
      return rehydrate();
    }
    return rawGet.apply(this, arguments);
  };

  proto.setItem = function (key, value) {
    try {
      if (key === SESSION_KEY && this === w.sessionStorage) persist(value);
      else if (key === TYPE_KEY && value && this === w.localStorage) { stampTypedAt(value); dropVisit(value); }
    } catch (e) { /* the mirror must never break the write it shadows */ }
    return rawSet.apply(this, arguments);
  };

  proto.removeItem = function (key) {
    try {
      // clearProgress() on submit, on ?retake, and on a version mismatch
      if (key === SESSION_KEY && this === w.sessionStorage) localDel(LONG_KEY);
    } catch (e) { /* noop */ }
    return rawDel.apply(this, arguments);
  };

  /* Read-only view for the strip and the Skin Profile empty state. */
  w.STS = w.STS || {};
  w.STS.noteFreshType = noteFreshType;
  w.STS.quizProgress = function () {
    var rec = readLong();
    if (!rec) return null;
    var total = rec.total || 0;
    return {
      answered: rec.answered,
      total: total,
      pct: total ? Math.round(rec.answered / total * 100) : 0,
      remaining: total > rec.answered ? total - rec.answered : 0,
      savedAt: rec.savedAt
    };
  };
}());
